import { Injectable, Injector } from '@angular/core';
import { get } from '@sama/micro-dash';
import { ajax } from 'jquery';
import { Subject } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { AssetLoadError } from '../../../../cross-team-shared/misc-services/resources/url-request.service';
import {
  SensorFrame2D,
  SensorRendererState,
} from '../../../../cross-team-shared/state/sensor-renderer-state';
import { LoadImageService } from '../../../../shared/misc-services/load-image.service';
import { ViewBox } from '../../../../team-apis/content/view-box';
import { Answer } from '../../../../team-apis/otterdam/answers/answer';
import { TaskExtras } from '../../../../team-apis/otterdam/task';
import {
  AbstractAssetInitializationService,
  AnnotationAsset,
} from '../../../shared/asset-loading/abstract-asset-initialization.service';
import {
  HACKY_VIEW_BOX_PADDING,
  ViewBoxService,
} from '../../../shared/utils/view-box.service';
import { ImageContentStore } from '../../state/image-content-store';
import { newMaskForLayer } from '../mask-utils';

interface Asset extends AnnotationAsset {
  image: HTMLImageElement;
  altImage?: HTMLImageElement;
  rawMask?: Uint8Array;
}

@Injectable()
export class ImageAssetInitializationService extends AbstractAssetInitializationService<Asset> {
  loadMaskTrigger$ = new Subject<{ answer: Answer; taskExtras?: TaskExtras }>();

  protected assetType = 'image';
  protected perFrameAssetLoadingService = undefined;
  private releaseInteractionNone?: VoidFunction;

  constructor(
    protected store: ImageContentStore,
    private loadImageService: LoadImageService,
    private viewboxService: ViewBoxService,
    injector: Injector,
  ) {
    super(injector);
    this.setUpLoadNewMaskTriggerSubscriptions();
  }

  async loadMask(maskUrl: string) {
    try {
      return await this.getRawMask(maskUrl);
    } catch (ex) {
      console.error(ex);
      throw new AssetLoadError('Failed to load image mask');
    }
  }

  protected override async loadAsset() {
    const { output, task, taskExtras } = this.store.state();
    if (task.assets_archived_at !== null) {
      throw new AssetLoadError(
        'Asset for this output has been archived. Task input and output answers are still retrievable through task export and API',
      );
    }

    const [loadResult, rawMask] = await Promise.all([
      this.loadImage(),
      this.loadMaskIfProvided(task.answers_or_gold[output.name], taskExtras),
    ]);

    return { ...loadResult, rawMask };
  }

  protected releaseFrame(_sensorFrame: SensorFrame2D) {}

  protected handleAssetLoadSuccess(asset: Asset, start: number) {
    this.store.assign({
      status: 'ready',
      sensorRendererState: this.buildSensorRendererState(asset),
      rawMask: asset.rawMask || newMaskForLayer(asset.image),
      // assign method only updates the properties defined in this object. we
      // need to set partialRawMask to undefined or else when updating the
      // store, the previous partialRawMask will persist and be painted to the
      // canvas
      partialRawMask: undefined,
    });

    const viewBox: ViewBox = [0, 0, asset.image.width, asset.image.height];
    this.viewboxService.initializeViewBox(viewBox, HACKY_VIEW_BOX_PADDING);

    this.trackAssetLoadEventIfNotPfal(asset, start);
    return {
      width: asset.image.width,
      height: asset.image.height,
      frameCount: 1,
      allFramesLoaded: true,
    };
  }

  protected override isPfalUrl() {
    return false;
  }

  private buildSensorRendererState(asset: Asset) {
    const sensorRendererState = new SensorRendererState<SensorFrame2D>(
      [
        {
          imageUrl: asset.image.src,
          crossOrigin: asset.image.crossOrigin,
          altImageUrl: asset.altImage?.src,
        },
      ],
      asset.image.height,
      asset.image.width,
    );
    return sensorRendererState;
  }

  private async loadMaskIfProvided(answer: Answer, taskExtras?: TaskExtras) {
    const unsignedUrl = get(answer, ['layers', 'raster_coding', 'mask_url']);
    if (!unsignedUrl) {
      return undefined;
    }

    const maskUrl = get(taskExtras, ['signed_urls', unsignedUrl, 0]);
    if (!maskUrl) {
      return undefined;
    }

    return this.loadMask(maskUrl);
  }

  private setUpLoadNewMaskTriggerSubscriptions() {
    this.subscribeTo(
      this.loadMaskTrigger$.pipe(
        switchMap(async ({ answer, taskExtras }) => {
          try {
            this.releaseInteractionNone ??=
              this.shellConnector.restrictInteractions('none');
            return await this.loadMaskIfProvided(answer, taskExtras);
          } catch (error) {
            this.handleAssetLoadFailure(error);
            return undefined;
          }
        }),
      ),
      // Async method should not be modifing the store because we can't ensure
      // that the chronology of events will be maintained. To avoid updating
      // the store with out of date values so we edit outside of the switchMap
      (rawMask) => {
        // fix me if you change this code
        // eslint-disable-next-line no-param-reassign -- fix me!
        rawMask ??= newMaskForLayer(this.store.state().sensorRendererState);
        this.store('rawMask').set(rawMask);
        this.releaseInteractionNone!();
        this.releaseInteractionNone = undefined;
      },
    );
  }

  private async loadImage() {
    const imageInputUrl = this.getInputUrl();
    if (!imageInputUrl) {
      throw new AssetLoadError('No asset url given');
    }

    try {
      const altImageInputUrl = this.store.getAltInputUrl();
      const [image, altImage] = await Promise.all([
        this.loadImageService.loadImage(imageInputUrl),
        altImageInputUrl
          ? this.loadImageService.loadImage(altImageInputUrl)
          : Promise.resolve(undefined),
      ]);

      const eventTrackingValue = {
        type: 'image',
        height: image.height,
        width: image.width,
      };
      return { image, eventTrackingValue, url: imageInputUrl, altImage };
    } catch (ex) {
      const reason = await determineReasonForImageLoadFailure(imageInputUrl);
      throw new AssetLoadError(reason, imageInputUrl);
    }
  }

  private async getRawMask(maskUrl: string) {
    const maskData = await this.loadImageService.loadMask(maskUrl);

    const rawMask = new Uint8Array(maskData.width * maskData.height);
    // fix me if you change this code
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- fix me!
    if (maskData) {
      for (let i = rawMask.length; i--; ) {
        rawMask[i] = maskData.data[i * 4];
      }
    }
    return rawMask;
  }
}

async function determineReasonForImageLoadFailure(supposedImageUrl: string) {
  return ajax({
    type: 'HEAD',
    url: supposedImageUrl,
  }).then(imageDiagnosticFetchSuccess, imageDiagnosticFetchFail);
}

function imageDiagnosticFetchSuccess(
  _body: any,
  _status: any,
  response: JQuery.jqXHR<any>,
) {
  if (isImage(response)) {
    return 'Image failed to render on canvas';
  } else {
    return 'URL does not point to an image';
  }
}

function imageDiagnosticFetchFail(response: JQuery.jqXHR<any>) {
  if (response.status === 404) {
    return 'Image does not exist at the given url';
    // https://stackoverflow.com/a/26451773
  } else if (response.status === 0) {
    return 'Image could not be accessed';
  } else {
    return 'Image failed to load';
  }
}

function isImage(response: JQuery.jqXHR<any>) {
  return /image/.test(response.getResponseHeader('content-type') || '');
}
